import type { TeamAllocationRequest } from '@/common/api/types/insights/teamAllocations';
import type { LocationSearchParams } from '@/common/api/types/location';
import type { ResourceFilterStatus } from '@/common/api/types/teams';
import type { OpenPositionFilledStatus } from '@/features/settings/api/types/openPositions';

export const capacityQueryKeys = {
  resources: {
    all: () => ['resources'],
    detail: (resourceId: number | null | undefined) => [
      ...capacityQueryKeys.resources.all(),
      'detail',
      resourceId,
    ],
    search: (filters: {
      query?: string;
      resourceStatus?: ResourceFilterStatus;
      locationIds?: number[];
      roleIds?: number[];
      skillIds?: number[];
    }) => [...capacityQueryKeys.resources.all(), 'search', filters],
  },
  workloadSchemes: {
    all: () => ['workloadSchemes'],
    search: (query: string) => [...capacityQueryKeys.workloadSchemes.all(), 'search', query],
    detail: ({ schemeId }: { schemeId: number }) => [
      ...capacityQueryKeys.workloadSchemes.all(),
      'detail',
      schemeId,
    ],
  },
  holidaySchemes: {
    all: () => ['holidaySchemes'],
    search: (query: string) => [...capacityQueryKeys.holidaySchemes.all(), 'search', query],
    detail: ({ schemeId }: { schemeId: number }) => [
      ...capacityQueryKeys.holidaySchemes.all(),
      'detail',
      schemeId,
    ],
    searchRanges: ({ schemeId, query }: { schemeId: number; query: string }) => [
      ...capacityQueryKeys.holidaySchemes.all(),
      'searchRanges',
      schemeId,
      query,
    ],
  },
  recentlySyncedSchemeIds: {
    all: () => ['recentlySyncedSchemeIds'],
  },
  locations: {
    all: () => ['locations'],
    search: (searchParams?: LocationSearchParams) => [
      ...capacityQueryKeys.locations.all(),
      'search',
      searchParams,
    ],
  },
  teamRoles: {
    all: () => ['team-roles'],
    search: (query: string) => [...capacityQueryKeys.teamRoles.all(), 'search', query],
  },
  teamRolesIncludingArchived: {
    all: () => ['team-roles-with-archived'],
    search: (query: string) => [
      ...capacityQueryKeys.teamRolesIncludingArchived.all(),
      'search',
      query,
    ],
  },
  skills: {
    all: () => ['skills'],
    search: (filters: { query?: string; isCritical?: boolean }) => [
      ...capacityQueryKeys.skills.all(),
      'search',
      filters,
    ],
  },
  openPositions: {
    all: () => ['open-positions'],
    detail: (id: number) => [...capacityQueryKeys.openPositions.all(), 'detail', id],
    search: (filters: {
      query?: string;
      filledStatus?: OpenPositionFilledStatus;
      roleIds?: number[];
      skillIds?: number[];
      locationIds?: number[];
      sortBy?: string;
      sortDirection?: 'ASC' | 'DESC';
    }) => [...capacityQueryKeys.openPositions.all(), 'search', filters],
    countByLoop: (loopId: number) => [...capacityQueryKeys.openPositions.all(), 'count', loopId],
    memberships: (id: number | undefined) => [
      ...capacityQueryKeys.openPositions.all(),
      'memberships',
      id,
    ],
    convertPreview: (id: number, resourceId: number | undefined) => [
      ...capacityQueryKeys.openPositions.all(),
      'convert-preview',
      id,
      resourceId,
    ],
  },
  capacityScope: {
    all: () => ['capacity-scope'],
    detail: (loopId: number) => [...capacityQueryKeys.capacityScope.all(), 'detail', loopId],
  },
  authStatus: {
    all: () => ['authStatus'],
  },
  orphanedInstances: {
    all: () => ['orphanedInstances'],
  },
  publicHolidays: {
    all: (year: number, countryCode: string) => ['publicHolidays', year, countryCode] as const,
    availableCountries: () => ['publicHolidays', 'availableCountries'] as const,
  },
  common: {
    schemeMembers: {
      all: () => ['scheme-members'],
      detail: (schemeId: number, query?: string) => ['scheme-members', { schemeId, query }],
    },
    membersNotInScheme: {
      all: () => ['members-not-in-scheme'],
      detail: (schemeId: number, query?: string) => ['members-not-in-scheme', { schemeId, query }],
    },
  },
  teamWorkAllocations: {
    all: () => ['team-work-allocations'],
    byLoopAndDateRange: (request: TeamAllocationRequest) => [
      ...capacityQueryKeys.teamWorkAllocations.all(),
      request,
    ],
  },
};
